import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { ConnectorTermsStatus, TermsChangedError, agreeToTerms } from '../services/api';

const ORANGE = '#ed8936';
const NOT_AGREED_MESSAGE = 'Please agree to the terms and conditions to continue';

interface Props {
  status: ConnectorTermsStatus;
  onAgreed: () => void;
  /** The terms changed while the dialog was open; reload them. */
  onStale: () => void;
  /** Leave live mode for the sample-data demo, which needs no agreement. */
  onSwitchToDemo?: () => void;
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Blocking terms and conditions checklist. There is no way to close it other
 * than ticking every term and agreeing.
 */
export default function TermsAgreementDialog({ status, onAgreed, onStale, onSwitchToDemo }: Props) {
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setChecked(new Set());
    setError(null);
  }, [status.version]);

  const toggle = (id: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setError(null);
  };

  const handleAgree = async () => {
    if (status.terms.some((t) => !checked.has(t.id))) {
      setError(NOT_AGREED_MESSAGE);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await agreeToTerms(status.version, status.terms.map((t) => t.id));
      onAgreed();
    } catch (e) {
      if (e instanceof TermsChangedError) {
        onStale();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed to record your agreement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const updatedOn = formatDate(status.requiredSince || status.updatedAt);

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 700, fontSize: '1.35rem', px: { xs: 2.5, sm: 4 }, pt: 3 }}>
        <GavelIcon sx={{ color: ORANGE, fontSize: 30 }} />
        Terms and conditions
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: { xs: 2.5, sm: 4 }, py: 3 }}>
        {status.previouslyAgreed && (
          <Alert severity="info" sx={{ fontSize: '0.95rem' }}>
            The terms and conditions have been updated{updatedOn ? ` on ${updatedOn}` : ''}. Please review and agree to continue.
          </Alert>
        )}
        <Typography sx={{ color: 'var(--color-text-subtle)', fontSize: '1rem' }}>
          Please read and tick each item below to continue using Super Connector.
        </Typography>
        {status.terms.map((term) => {
          const isChecked = checked.has(term.id);
          return (
            <Box
              key={term.id}
              onClick={() => toggle(term.id)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 1.5,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: isChecked ? 'rgba(237, 137, 54, 0.6)' : 'var(--color-border)',
                bgcolor: isChecked ? 'rgba(237, 137, 54, 0.06)' : 'transparent',
              }}
            >
              <Checkbox
                checked={isChecked}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggle(term.id)}
                sx={{ p: 0.5, mt: -0.25, '& .MuiSvgIcon-root': { fontSize: 28 }, '&.Mui-checked': { color: ORANGE } }}
                inputProps={{ 'aria-label': `Agree to term ${term.sortOrder}` }}
              />
              <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 1.7, fontSize: { xs: '1rem', sm: '1.075rem' } }}>
                {term.body}
              </Typography>
            </Box>
          );
        })}
        {error && <Alert severity="error" sx={{ fontSize: '0.95rem' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, py: 2, gap: 1, flexWrap: 'wrap' }}>
        {onSwitchToDemo && (
          <Button
            variant="outlined"
            size="large"
            startIcon={<RocketLaunchIcon />}
            onClick={onSwitchToDemo}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
              mr: 'auto',
              color: ORANGE,
              borderColor: ORANGE,
              '&:hover': { borderColor: '#dd7826', bgcolor: 'rgba(237, 137, 54, 0.08)' },
            }}
          >
            View Demo
          </Button>
        )}
        <Button
          variant="contained"
          size="large"
          onClick={() => { void handleAgree(); }}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', minWidth: 140, fontSize: '1rem' }}
        >
          {submitting ? 'Saving…' : 'Agree'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
